import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle, 
  Play, 
  ArrowRight, 
  FileText, 
  Users, 
  Settings,
  Zap,
  Target,
  BookOpen,
  Video
} from "lucide-react";
import { useTrialManagement } from "@/hooks/useTrialManagement";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  estimatedTime: number;
  valuePoints: string[];
  isCompleted: boolean;
  isActive: boolean;
}

export const OnboardingFlow: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const { trackEngagement, trackFunnelStage } = useTrialManagement();

  const [steps, setSteps] = useState<OnboardingStep[]>([
    {
      id: 'upload_first_bill',
      title: 'Upload Your First Bill',
      description: 'Experience our AI-powered extraction technology',
      icon: FileText,
      estimatedTime: 2,
      valuePoints: [
        'See 95%+ accurate data extraction',
        'Save 10+ minutes per bill',
        'Reduce manual data entry errors'
      ],
      isCompleted: false,
      isActive: true
    },
    {
      id: 'setup_integrations',
      title: 'Connect Your Tools',
      description: 'Integrate with your existing workflow',
      icon: Settings,
      estimatedTime: 5,
      valuePoints: [
        'Seamless data flow to your ERP',
        'Eliminate duplicate data entry',
        'Real-time synchronization'
      ],
      isCompleted: false,
      isActive: false
    },
    {
      id: 'invite_team',
      title: 'Invite Your Team',
      description: 'Collaborate efficiently with colleagues',
      icon: Users,
      estimatedTime: 3,
      valuePoints: [
        'Streamline approval workflows',
        'Real-time collaboration',
        'Role-based permissions'
      ],
      isCompleted: false,
      isActive: false
    },
    {
      id: 'explore_analytics',
      title: 'Explore Analytics',
      description: 'Discover insights from your data',
      icon: Target,
      estimatedTime: 4,
      valuePoints: [
        'Track spending patterns',
        'Identify cost savings',
        'Automated reporting'
      ],
      isCompleted: false,
      isActive: false
    }
  ]);

  const completedSteps = steps.filter(step => step.isCompleted).length;
  const progressPercentage = (completedSteps / steps.length) * 100;

  const handleStartStep = async (stepIndex: number) => {
    const step = steps[stepIndex];
    
    // Track engagement
    await trackEngagement.mutateAsync({
      feature_used: step.id,
      session_duration_minutes: step.estimatedTime,
      actions_performed: 1,
      value_realized: 0.25 * (stepIndex + 1) // Progressive value realization
    });

    // Track funnel progression
    await trackFunnelStage.mutateAsync({
      funnel_stage: stepIndex === 0 ? 'feature_discovery' : 'value_realization',
      stage_data: { step: step.id, step_index: stepIndex }
    });

    // Mark step as completed and activate next step
    setSteps(prev => prev.map((s, i) => ({
      ...s,
      isCompleted: i === stepIndex ? true : s.isCompleted,
      isActive: i === stepIndex + 1 ? true : (i <= stepIndex ? false : s.isActive)
    })));

    setCurrentStep(Math.min(stepIndex + 1, steps.length - 1));
  };

  const handleSkipStep = (stepIndex: number) => {
    setSteps(prev => prev.map((s, i) => ({
      ...s,
      isActive: i === stepIndex + 1 ? true : (i <= stepIndex ? false : s.isActive)
    })));
    setCurrentStep(Math.min(stepIndex + 1, steps.length - 1));
  };

  useEffect(() => {
    // Track onboarding start
    if (completedSteps === 0) {
      trackFunnelStage.mutate({
        funnel_stage: 'first_login',
        stage_data: { onboarding_started: true }
      });
    }
  }, []);

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Quick Start Guide
            </CardTitle>
            <CardDescription>
              Complete these steps to unlock the full potential of your trial
            </CardDescription>
          </div>
          <Badge variant="secondary" className="text-lg px-3 py-1">
            {completedSteps}/{steps.length}
          </Badge>
        </div>
        
        <div className="mt-4">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Progress</span>
            <span>{Math.round(progressPercentage)}% Complete</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isCurrentStep = index === currentStep;

          return (
            <div
              key={step.id}
              className={`relative p-4 rounded-lg border transition-all ${
                step.isCompleted 
                  ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' 
                  : step.isActive
                    ? 'bg-primary/5 border-primary/20 ring-1 ring-primary/20'
                    : 'bg-muted/30 border-muted'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                  step.isCompleted 
                    ? 'bg-green-500 text-white' 
                    : step.isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                }`}>
                  {step.isCompleted ? (
                    <CheckCircle className="h-6 w-6" />
                  ) : (
                    <Icon className="h-6 w-6" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-foreground">{step.title}</h3>
                    <span className="text-sm text-muted-foreground">
                      ~{step.estimatedTime} min
                    </span>
                  </div>
                  
                  <p className="text-muted-foreground text-sm mb-3">
                    {step.description}
                  </p>

                  <div className="space-y-1 mb-4">
                    {step.valuePoints.map((point, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Zap className="h-3 w-3 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">{point}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    {step.isCompleted ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Completed
                      </Badge>
                    ) : step.isActive ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleStartStep(index)}
                          disabled={trackEngagement.isPending}
                          className="bg-primary hover:bg-primary/90"
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Start Step
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSkipStep(index)}
                        >
                          Skip for Now
                        </Button>
                      </>
                    ) : (
                      <Badge variant="secondary">Waiting</Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Video/Tutorial Link */}
              {step.isActive && (
                <div className="mt-4 p-3 bg-accent/50 rounded-lg border border-accent">
                  <div className="flex items-center gap-2 text-sm">
                    <Video className="h-4 w-4 text-primary" />
                    <span className="font-medium">Need help?</span>
                    <Button variant="link" size="sm" className="p-0 h-auto">
                      Watch tutorial →
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {completedSteps === steps.length && (
          <div className="text-center py-6 space-y-3">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-foreground">
              Congratulations! 🎉
            </h3>
            <p className="text-muted-foreground">
              You've completed the onboarding. Ready to upgrade and unlock advanced features?
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90">
              Upgrade Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};